import React, { useContext } from "react";
import {
  useProjectResourcesRetrieval,
  useRetrieveCurrentPlanzone,
  useRetrieveResourceGroups,
  useRetrieveWorkLoads,
} from "@planzone/view-models";
import {
  Group as G,
  Identifiable,
  project,
  resource,
  RetrieveWorkLoadsFilter,
  WorkLoadsCalendar,
} from "@planzone/dto";

import { useWorkspaceContext } from "@scenes/workspaceContext";
import { Filter } from "@common/filter";
import { Views } from "../api/views";
import { Context } from "../context";
import { useResources, sortWorkLoads } from "../utils";
import { Props } from "./types";
import { Calendar } from "./calendar";
import styles from "./styles.scss";
import { Group } from "./group";

function WorkLoads({ dates, filter: f }: Props) {
  const { cell, currentView, areWorkloadsGroupsToggled } = useContext(Context);
  const { resources } = useResources();
  const filter = {
    ...(resources.length > 0
      ? {
          resources: resources,
        }
      : {}),
    view: (currentView === Views.YEARLY
      ? "year"
      : currentView) as RetrieveWorkLoadsFilter["view"],
    startDate: dates[0].toUTC().toISO() ?? "",
    endDate: dates[dates.length - 1].toUTC().toISO() ?? "",
  };
  const {
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    isDirty,
    workLoads,
    retrieveWorkLoads,
  } = useRetrieveWorkLoads(filter);
  const { planzone } = useRetrieveCurrentPlanzone();
  const ctx = useWorkspaceContext();
  const isInProject = ctx.type === "project";
  const { groups } = useRetrieveResourceGroups(planzone.uuid);
  const { groups: projectGroups } = useRetrieveResourceGroups(
    isInProject ? ctx.project.uuid : project.uuid,
  );
  const { projectResources } = useProjectResourcesRetrieval(
    isInProject ? ctx.project : project,
  );
  const planzoneGroups = isInProject
    ? groups.reduce<Identifiable<G>[]>((filteredGroups, group) => {
        const filteredGroup = {
          ...group,
          resources: group.resources.filter(
            ({ uuid }) =>
              !!projectResources.find(({ resource }) => resource.uuid === uuid),
          ),
        };

        return filteredGroup.resources.length > 0
          ? [...filteredGroups, filteredGroup]
          : filteredGroups;
      }, [])
    : groups;

  React.useEffect(() => {
    ((!isRequestPending && !isRequestFailure && !isRequestSuccess) ||
      (isDirty && !isRequestPending)) &&
      retrieveWorkLoads(filter);
  }, [filter.startDate, filter.endDate, filter.view, isDirty]);

  return (
    <div
      className={styles.calendar}
      style={{
        height:
          workLoads.length * cell.height +
          (cell.height + (cell.height + cell.height * (1 / 3))),
      }}
    >
      {areWorkloadsGroupsToggled ? (
        <>
          {planzoneGroups
            .reduce<(Identifiable<G> & { workloads: WorkLoadsCalendar })[]>(
              (nextGroups, group) => [
                ...nextGroups,
                {
                  ...group,
                  workloads: sortWorkLoads(
                    group.resources.reduce<WorkLoadsCalendar>(
                      (workloads, resource) => {
                        const workload = workLoads.find(
                          ({ uuid }) => uuid === resource.uuid,
                        );

                        return workload ? [...workloads, workload] : workloads;
                      },
                      [],
                    ),
                  ),
                },
              ],
              [],
            )
            .map((group) => (
              <Group filter={f} group={group} dates={dates} key={group.uuid} />
            ))}
          {projectGroups
            .reduce<(Identifiable<G> & { workloads: WorkLoadsCalendar })[]>(
              (nextGroups, group) => [
                ...nextGroups,
                {
                  ...group,
                  workloads: sortWorkLoads(
                    group.resources.reduce<WorkLoadsCalendar>(
                      (workloads, { resource }) => {
                        const workload = workLoads.find(
                          ({ uuid }) => uuid === resource.uuid,
                        );

                        return workload ? [...workloads, workload] : workloads;
                      },
                      [],
                    ),
                  ),
                },
              ],
              [],
            )
            .map((group) => (
              <Group filter={f} group={group} dates={dates} key={group.uuid} />
            ))}
        </>
      ) : (
        <Filter
          filter={f}
          data={sortWorkLoads(workLoads)}
          extractCriteria={(workLoad) => ({
            resource: {
              ...resource,
              uuid: workLoad.uuid,
              name: workLoad.resourceName,
              profession: workLoad.resourceTitle || "",
              tags: workLoad.resourceTags.map((name) => ({ name })),
            },
          })}
        >
          {(workLoads) => (
            <Calendar
              dates={dates}
              workLoads={workLoads}
              isGroupCalendar={false}
            />
          )}
        </Filter>
      )}
    </div>
  );
}

export { WorkLoads };
